import { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";

import { serverResponseFormat, shortMonthAndYear } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { useFloatingFrameProps } from "app/components/ai-kit/FloatingFrameProvider";
import DemandSpikeClusterExplanation from "./DemandSpikeClusterExplanation";
import DemandTrendsAnomalyAgentError from "./DemandTrendsAnomalyAgentError";
import DemandTrendsAnomalyAgentPlaceHolder from "./DemandTrendsAnomalyAgentPlaceHolder";
import { fetchDemandSpikeThreadID } from "../demand-over-time/common/fetchData";
import { useDemandSpikeDrivingKWClusters } from "../demand-over-time/common/fetchDataHooks";
import countryService from "app/services/CountryService";

interface IDemandTrendsSpikeFeedContainerProps {
    matchedDate: string;
    country: string;
    keywordListId: string;
    keywordListName: string;
}

export const DemandTrendsSpikeFeedContainer: React.FC<IDemandTrendsSpikeFeedContainerProps> = ({
    matchedDate,
    keywordListId,
    keywordListName,
    country,
}) => {
    const [thread, setThread] = useState<{
        threadId: string;
        country: string;
        matchedDate: string;
        keywordListId: string;
    }>();
    const [threadError, setThreadError] = useState(false);
    const { setIsVisible } = useFloatingFrameProps();
    const threadIsReady = Boolean(thread?.threadId && thread?.keywordListId && thread?.matchedDate && thread?.country);
    const spikeKWClusters = useDemandSpikeDrivingKWClusters(
        thread?.threadId || "",
        keywordListId || "",
        matchedDate || "",
        country || "",
    );

    const isCompleted = threadIsReady && spikeKWClusters.isSuccess && spikeKWClusters.data?.state === "completed";
    const isFailed =
        threadError ||
        (threadIsReady &&
            (spikeKWClusters.isError || (spikeKWClusters.isSuccess && spikeKWClusters.data?.state === "failed")));

    const getLoadingFrame = () => {
        const formattedDate = dayjs(matchedDate, serverResponseFormat).format(shortMonthAndYear);
        const countryName = countryService.getCountryById(country)?.text;
        return (
            <DemandTrendsAnomalyAgentPlaceHolder
                keywordListName={keywordListName}
                date={formattedDate}
                country={countryName}
            />
        );
    };

    const explanationRef = useRef<HTMLDivElement | null>(null);

    // Scroll into view when the explanation is rendered
    useEffect(() => {
        if (isCompleted && explanationRef.current) {
            explanationRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [isCompleted]);

    useEffect(() => {
        setIsVisible(true);
        setThread(undefined);
        setThreadError(false);
        fetchDemandSpikeThreadID()
            .then((result) => {
                setThread({ threadId: result?.threadId, keywordListId, country, matchedDate });
            })
            .catch((e) => {
                Sentry.errorTracker({
                    e,
                    message: "Failed to create demand-spike agent thread",
                    level: "error",
                    params: { extra: { keywordListId, country, matchedDate } },
                });
                setThreadError(true);
            });
    }, [keywordListId, country, matchedDate, setIsVisible]);

    if (isFailed) {
        return <DemandTrendsAnomalyAgentError />;
    }

    if (isCompleted && spikeKWClusters.data) {
        return (
            <div
                ref={explanationRef}
                data-automation="demand-spike-feed-explanation"
            >
                <DemandSpikeClusterExplanation data={spikeKWClusters.data} />
            </div>
        );
    }

    return getLoadingFrame();
};
